import React, {useEffect, useState} from "react";
import dayjs from "dayjs";
import AttachmentCollapsibleList from "../../../components/files/AttachmentCollapsibleList";
import {getFile, getImagesAndFiles} from "../../../api/file/FileService";
import {formatDeadLineDisplay, formatWorkCloseDateDisplay} from "./workProcessUtils";
import {formatSimmsDateTime, toKSTISOString} from "@/src/components/commonmethod/CommonMethod";

const IMG_BASE = import.meta.env.VITE_IMG_BASE;

export function formatActivityTime(value) {
    if (!value) return undefined;
    const text = String(value);
    const normalized = text.includes("T") ? text.replace("T", " ") : text;
    const parsed = dayjs(normalized);
    if (!parsed.isValid()) return normalized.slice(0, 19);

    const diffMinutes = dayjs().diff(parsed, "minute");
    if(diffMinutes >= 0){
        if (diffMinutes < 1 ) return "방금 전";
        if (diffMinutes < 60) return `${diffMinutes}분 전`;
        const diffHours = dayjs().diff(parsed, "hour");
        if (diffHours < 24) return `${diffHours}시간 전`;
        const diffDays = dayjs().startOf("day").diff(parsed.startOf("day"), "day");
        if (diffDays === 0) return "오늘";
        if (diffDays === 1) return "어제";
        if (diffDays < 30) return `${diffDays}일 전`;
        if (diffDays < 365) return `${Math.floor(diffDays / 30)}개월 전`;
    }else{
        return formatWorkCloseDateDisplay(normalized)
    }





    console.log("1");
    return parsed.format("YYYY-MM-DD");
}

function useActivityTableFiles(tableInfo) {
    const [items, setItems] = useState(undefined);

    useEffect(() => {
        if (!tableInfo?.joinKey || !tableInfo?.tableName) {
            setItems([]);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            try {
                const response = await getImagesAndFiles(
                    tableInfo.tableName,
                    tableInfo.joinKey,
                    "FILE",
                );
                if (cancelled) return;
                const data = response?.status === 200 && Array.isArray(response.data)
                    ? response.data.filter(Boolean)
                    : [];
                setItems(data);
            } catch {
                if (!cancelled) setItems([]);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [tableInfo?.joinKey, tableInfo?.tableName]);

    return items;
}

export function TaskActivityAttachments({tableInfo, files, className = "task-activity-comment__files"}) {
    const inlineFiles = Array.isArray(files) ? files.filter(Boolean) : [];
    const shouldFetchTable = inlineFiles.length === 0 && Boolean(tableInfo?.joinKey);
    const tableFiles = useActivityTableFiles(shouldFetchTable ? tableInfo : null);

    if (inlineFiles.length > 0) {
        return (
            <div className={className}>
                <AttachmentCollapsibleList
                    items={inlineFiles}
                    onOpen={(item) => getFile(item)}
                />
            </div>
        );
    }

    if (!shouldFetchTable || tableFiles === undefined || tableFiles.length === 0) {
        return null;
    }

    return (
        <div className={className}>
            <AttachmentCollapsibleList
                items={tableFiles}
                onOpen={(item) => getFile(item)}
            />
        </div>
    );
}

export function TaskActivityFeed({children, sortControl}) {
    return (
        <div className="task-activity-feed">
            {sortControl}
            {children}
        </div>
    );
}

export function TaskActivityFeedSort({value = "latest", onChange}) {
    const setSort = (order) => {
        if (order !== value) onChange?.(order);
    };

    return (
        <div className="task-activity-feed__sort" role="group" aria-label="활동 정렬">
            <button
                type="button"
                className={`task-activity-feed__sort-btn${value === "latest" ? " is-active" : ""}`}
                aria-pressed={value === "latest"}
                onClick={() => setSort("latest")}
            >
                최근 작업순
            </button>
            <span className="task-activity-feed__sort-sep" aria-hidden="true">|</span>
            <button
                type="button"
                className={`task-activity-feed__sort-btn${value === "chrono" ? " is-active" : ""}`}
                aria-pressed={value === "chrono"}
                onClick={() => setSort("chrono")}
            >
                시간순
            </button>
        </div>
    );
}

export function TaskActivityAvatar({user, name}) {
    const displayName = user?.name || name || "?";
    const initial = displayName.slice(0, 1);

    if (user?.profileImage) {
        return (

            <img
                src={`${IMG_BASE}${user.profileImage}`}
                alt=""
                className="task-activity-avatar task-activity-avatar--img"
            />
        );
    }

    return (
        <span className="task-activity-avatar task-activity-avatar--initial" aria-hidden="true">
            {initial}
        </span>
    );
}

function TaskActivityCommentMeta({name, label, time, labelTone}) {
    // const activityTime = formatActivityTime(time);
    const activityTime = formatWorkCloseDateDisplay(time);

    return (
        <p className="task-activity-comment__meta">
            {name && (
                <span className="task-activity-comment__author">{name}</span>
            )}
            {label && (
                <>
                    <span className="task-activity-comment__sep" aria-hidden="true">·</span>
                    <span className={`task-activity-comment__label${labelTone ? ` task-activity-comment__label--${labelTone}` : ""}`}>
                        {label}
                    </span>
                </>
            )}
            {activityTime && (
                <>
                    <span className="task-activity-comment__sep" aria-hidden="true">·</span>
                    <time className="task-activity-comment__time" dateTime={time}>
                        {activityTime}
                    </time>
                </>
            )}
        </p>
    );
}

export function TaskActivityWorkSection({name, time, workDeadLine, fileTable, files, children}) {
    const activityTime = formatActivityTime(time);
    const deadLineText = formatDeadLineDisplay(workDeadLine);

    const metaDates = deadLineText ? (
        <span className="task-activity-work__meta-dates">
            <span className="task-activity-work__work-date">
                <span className="task-activity-work__work-date-label">마감일</span>
                <time className="task-activity-work__work-date-value" dateTime={workDeadLine}>
                    {deadLineText}
                </time>
            </span>
        </span>
    ) : null;

    return (
        <div className="task-activity-work">
            {(name || activityTime || deadLineText) && (
                <p className="task-activity-work__meta">
                    <span className="task-activity-work__meta-start">
                        {name && (
                            <span className="task-activity-work__author">{name}</span>
                        )}
                        {activityTime && (
                            <>
                                {name && (
                                    <span className="task-activity-work__sep" aria-hidden="true">·</span>
                                )}
                                <time className="task-activity-work__time" dateTime={time}>
                                    {activityTime}
                                </time>
                            </>
                        )}
                    </span>
                    {metaDates}
                </p>
            )}
            {children}
            <TaskActivityAttachments
                tableInfo={fileTable}
                files={files}
                className="task-activity-work__files"
            />
        </div>
    );
}

export function TaskActivityCommentRow({
    user,
    name,
    time,
    label,
    labelTone,
    children,
}) {
    const displayName = user?.name || name || "";

    return (
        <div className="task-activity-comment">
            <div className="task-activity-comment__main">
                {(displayName || label || time) && (
                    <div className="task-activity-comment__header">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                             stroke="currentColor" className="size-4">
                            <path stroke-linecap="round" stroke-linejoin="round"
                                  d="m16.49 12 3.75 3.75m0 0-3.75 3.75m3.75-3.75H3.74V4.499"/>
                        </svg>


                        {/*<TaskActivityAvatar user={user} name={displayName}/>*/}
                        <TaskActivityCommentMeta
                            name={displayName || undefined}
                            label={label}
                            labelTone={labelTone}
                            time={time}
                        />
                    </div>
                )}
                {children}
            </div>
        </div>
    );
}

export function TaskActivityWorkCompose({user, name, time, workDate, workDeadLine, metaLabel, showAvatar, children}) {
    const displayName = user?.name || name || "";
    const activityTime = formatActivityTime(time);
    const workDateText = formatWorkCloseDateDisplay(workDate);
    const workDeadLineText = formatDeadLineDisplay(workDeadLine);
    const showMeta = Boolean(displayName || activityTime || workDateText || workDeadLineText || metaLabel);

    const metaDates = (workDeadLineText || workDateText) ? (
        <span className="task-activity-work__meta-dates">
               {workDateText && (
                   <span className="task-activity-work__work-date">
                    <span className="task-activity-work__work-date-label">작업 완료시간</span>
                    <time className="task-activity-work__work-date-value" dateTime={workDate}>
                        {workDateText}
                    </time>
                </span>
               )}

            {workDeadLineText && (
                <span className="task-activity-work__work-date">
                    <span className="task-activity-work__work-date-label">마감일</span>
                    <time className="task-activity-work__work-date-value" dateTime={workDeadLine}>
                        {workDeadLineText}
                    </time>
                </span>
            )}

        </span>
    ) : null;

    const metaContent = (
        <p className={`task-activity-work__meta${showAvatar ? " task-activity-work__meta--with-avatar" : ""}`}>
            <span className="task-activity-work__meta-start">
                {displayName && (
                    <span className="task-activity-work__author">{displayName}</span>
                )}
                {metaLabel && (
                    <>
                        {displayName && (
                            <span className="task-activity-work__sep" aria-hidden="true">·</span>
                        )}
                        <span className="task-activity-work__label">{metaLabel}</span>
                    </>
                )}
                {activityTime && (
                    <>
                        {(displayName || metaLabel) && (
                            <span className="task-activity-work__sep" aria-hidden="true">·</span>
                        )}
                        <time className="task-activity-work__time" dateTime={time}>
                            {activityTime}
                        </time>
                    </>
                )}
            </span>
            {metaDates}
        </p>
    );

    return (
        <div className="task-activity-work task-activity-work--compose">
            {showMeta && (
                showAvatar ? (
                    <div className="task-activity-work__header">
                        {/*<TaskActivityAvatar user={user} name={displayName} />*/}
                        {metaContent}
                    </div>
                ) : metaContent
            )}
            <div className="task-activity-work__compose">{children}</div>
        </div>
    );
}

export function TaskActivityCycleCompose({workSection, verificationSection, className}) {
    const hasWork = Boolean(workSection);
    const hasVerification = Boolean(verificationSection);
    if (!hasWork && !hasVerification) return null;

    const itemClassName = ["task-activity-cycle", "task-activity-cycle--compose", className]
        .filter(Boolean)
        .join(" ");

    return (
        <article className={itemClassName}>
            {hasWork && workSection}
            {hasVerification && verificationSection}
        </article>
    );
}

export function TaskActivityItem({user, name, children, className, variant = "work", time, workDate, workDeadLine, label, labelTone}) {
    const displayName = user?.name || name || "";

    if (variant === "comment") {
        return (
            <TaskActivityCycleCompose
                className={className}
                verificationSection={(
                    <TaskActivityCommentRow
                        user={user}
                        name={displayName}
                        time={time}
                        label={label}
                        labelTone={labelTone}
                    >
                        <div className="task-activity-comment__compose">{children}</div>
                    </TaskActivityCommentRow>
                )}
            />
        );
    }

    return (
        <TaskActivityCycleCompose
            className={className}
            workSection={(
                <TaskActivityWorkCompose user={user} name={displayName} time={time} workDate={workDate} workDeadLine={workDeadLine}>
                    {children}
                </TaskActivityWorkCompose>
            )}
        />
    );
}

function formatDeadlineChangeText(deadlineChange) {
    if (!deadlineChange) return "";
    const after = formatWorkCloseDateDisplay(deadlineChange.after) || deadlineChange.after || "-";
    const before = formatWorkCloseDateDisplay(deadlineChange.before) || deadlineChange.before;
    return before ? `${before} → ${after}` : after;
}

export function TaskActivityCycleCard({
    user,
    name,
    time,
    workBody,
    workDeadLine,
    verification,
    verificationBody,
    fileTable,
    files,
    verificationCompose,
}) {
    const reviewerName = verification?.name;
    const showVerification = Boolean(verification && (
        verification.passed
        || verificationBody
        || verification.deadlineChange
        || reviewerName
    ));
    const showVerificationCompose = Boolean(verificationCompose);
    const hasDeadline = Boolean(verification?.deadlineChange) && !verification?.passed;
    const hasWork = Boolean(workBody || fileTable || files?.length > 0);
    const cycleClassName = [
        "task-activity-cycle",
        showVerificationCompose ? "task-activity-cycle--compose" : "",
    ].filter(Boolean).join(" ");

    return (
        <article className={cycleClassName}>
            {hasWork && (
                <TaskActivityWorkSection
                    name={name}
                    time={time}
                    workDeadLine={workDeadLine}
                    fileTable={fileTable}
                    files={files}
                >
                    {workBody && (
                        <div className="task-activity-work__body">{workBody}</div>
                    )}
                </TaskActivityWorkSection>
            )}
            {showVerification && (
                <div className={`task-activity-cycle__comments${hasWork ? "" : " task-activity-cycle__comments--solo"}`}>
                    <TaskActivityCommentRow
                        user={verification.user}
                        name={reviewerName}
                        time={time}
                        label={verification.passed ? "검수완료" : "검수"}
                        labelTone={verification.passed ? "pass" : undefined}
                    >
                        {verificationBody && (
                            <div className="task-activity-comment__body">{verificationBody}</div>
                        )}
                        {hasDeadline && (
                            <p className="task-activity-comment__note">
                                마감일
                                {" "}
                                {formatDeadlineChangeText(verification.deadlineChange)}
                            </p>
                        )}
                    </TaskActivityCommentRow>
                </div>
            )}
            {showVerificationCompose && verificationCompose}
        </article>
    );
}

/** @deprecated */
export function TaskActivityWorkContent({children}) {
    if (!children) return null;
    return <div className="task-activity-work__body">{children}</div>;
}

/** @deprecated */
export function TaskActivityVerificationBlock() {
    return null;
}

export function TaskActivitySubLine({children}) {
    if (!children) return null;
    return <p className="task-activity-comment__note">{children}</p>;
}

/** @deprecated use TaskActivityItem */
export const TaskProgressThreadItem = TaskActivityItem;
